import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import AuthService from "../AuthService";
import axiosInstance from "../axiosInstance";

function SchoolAIPage() {
  const user = AuthService.getCurrentUser();
  const [policy, setPolicy] = useState(null);
  const [usage, setUsage] = useState(null);
  const [message, setMessage] = useState("");
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [booting, setBooting] = useState(true);

  const roleConfig = useMemo(() => getRoleConfig(user?.user_type), [user?.user_type]);

  const loadAI = async () => {
    try {
      setBooting(true);
      const [policyResponse, usageResponse] = await Promise.all([
        axiosInstance.get("ai/policy/"),
        axiosInstance.get("ai/usage/"),
      ]);
      setPolicy(policyResponse.data || {});
      setUsage(usageResponse.data || {});
    } catch (error) {
      console.error(error);
      toast.error("Failed to load AI assistant status.");
    } finally {
      setBooting(false);
    }
  };

  useEffect(() => {
    loadAI();
  }, []);

  const askAI = async (event) => {
    event.preventDefault();
    const prompt = message.trim();
    if (!prompt) {
      toast.warning("Type your question first.");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post("ai/assistant/", { message: prompt });
      const answer = response.data?.answer || "";
      setAnswers((previous) => [
        {
          id: Date.now(),
          question: prompt,
          answer,
          model: response.data?.model,
        },
        ...previous,
      ]);
      setMessage("");
      await loadAI();
    } catch (error) {
      const detail =
        error.response?.data?.detail ||
        Object.values(error.response?.data || {})?.flat?.()?.[0] ||
        "AI assistant could not answer right now.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const remainingToday =
    policy?.remaining_today ??
    Math.max(0, Number(policy?.daily_limit || 0) - Number(policy?.used_today || 0));

  return (
    <div className="container-fluid py-4">
      <div className="card border-0 shadow-sm mb-3 overflow-hidden">
        <div
          className="card-body p-4"
          style={{ background: "linear-gradient(135deg, #eef8f1, #ffffff)" }}
        >
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <small className="text-uppercase fw-bold text-success">{roleConfig.kicker}</small>
              <h3 className="fw-bold mb-1" style={{ color: "#125b2d" }}>
                {roleConfig.title}
              </h3>
              <p className="text-muted mb-0">{roleConfig.subtitle}</p>
            </div>
            <button className="btn btn-light border" onClick={loadAI} disabled={booting}>
              <i className="bi bi-arrow-clockwise me-2"></i>
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <SummaryCard label="Daily Limit" value={policy?.daily_limit ?? "-"} icon="bi-speedometer2" />
        <SummaryCard label="Used Today" value={policy?.used_today ?? "-"} icon="bi-chat-dots" />
        <SummaryCard label="Remaining" value={remainingToday} icon="bi-battery-half" />
        <SummaryCard label="Access" value={policy?.allowed ? "Active" : "Restricted"} icon="bi-shield-check" />
      </div>

      {!booting && !policy?.allowed && (
        <div className="alert alert-warning border-0 shadow-sm">
          <div className="fw-bold mb-1">AI access is currently restricted</div>
          <div>{policy?.reason || "Your account is not currently allowed to use AI."}</div>
          {policy?.fee_status?.outstanding_balance > 0 && (
            <small className="d-block mt-1 text-muted">
              Outstanding balance: {policy.fee_status.outstanding_balance}
            </small>
          )}
        </div>
      )}

      <div className="row g-3">
        <div className="col-xl-7">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white">
              <h6 className="fw-bold mb-0">
                <i className={`bi ${roleConfig.icon} me-2 text-success`}></i>
                Ask the Assistant
              </h6>
            </div>
            <div className="card-body">
              <form onSubmit={askAI}>
                <textarea
                  className="form-control"
                  rows="6"
                  maxLength="2000"
                  placeholder={roleConfig.placeholder}
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  disabled={loading || !policy?.allowed}
                ></textarea>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-2">
                  <small className="text-muted">{message.length}/2000 characters</small>
                  <button className="btn btn-success" disabled={loading || !policy?.allowed}>
                    {loading ? "Thinking..." : "Ask AI"}
                  </button>
                </div>
              </form>

              <div className="alert alert-light border mt-3 mb-0">
                <strong>Guardrail:</strong> {roleConfig.guardrail}
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-5">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white">
              <h6 className="fw-bold mb-0">
                <i className="bi bi-lightbulb me-2 text-success"></i>
                Helpful prompts
              </h6>
            </div>
            <div className="card-body">
              <div className="d-grid gap-2">
                {roleConfig.prompts.map((prompt) => (
                  <button
                    type="button"
                    className="btn btn-light border text-start"
                    key={prompt}
                    onClick={() => setMessage(prompt)}
                    disabled={!policy?.allowed}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm mt-3">
        <div className="card-header bg-white">
          <h6 className="fw-bold mb-0">Recent AI Answers</h6>
        </div>
        <div className="card-body">
          {answers.length === 0 ? (
            <div className="text-center text-muted py-4">
              Your answers for this session will appear here.
            </div>
          ) : (
            answers.map((item) => (
              <div className="border rounded-3 p-3 mb-3" key={item.id}>
                <div className="fw-bold mb-1">You asked:</div>
                <p className="mb-2">{item.question}</p>
                <div className="fw-bold mb-1">Assistant:</div>
                <div style={{ whiteSpace: "pre-wrap" }}>{item.answer}</div>
                {item.model && <small className="text-muted d-block mt-2">Model: {item.model}</small>}
              </div>
            ))
          )}
        </div>
      </div>

      {usage?.logs?.length > 0 && (
        <div className="card border-0 shadow-sm mt-3">
          <div className="card-header bg-white">
            <h6 className="fw-bold mb-0">Usage History</h6>
          </div>
          <div className="card-body">
            {usage.logs.slice(0, 8).map((log) => (
              <div className="border-bottom py-2" key={log.id}>
                <div className="d-flex justify-content-between gap-2">
                  <div className="fw-semibold">{log.prompt}</div>
                  <span className={`badge ${log.status === "ALLOWED" ? "text-bg-success" : log.status === "BLOCKED" ? "text-bg-warning" : "text-bg-danger"}`}>
                    {log.status}
                  </span>
                </div>
                {log.block_reason && <small className="text-muted">{log.block_reason}</small>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, icon }) {
  return (
    <div className="col-6 col-xl-3">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body d-flex justify-content-between align-items-center">
          <div>
            <small className="text-muted fw-bold">{label}</small>
            <h4 className="fw-bold mb-0">{value}</h4>
          </div>
          <div className="rounded-3 p-3 text-success" style={{ background: "#eef8f1" }}>
            <i className={`bi ${icon} fs-4`}></i>
          </div>
        </div>
      </div>
    </div>
  );
}

function getRoleConfig(userType) {
  if (userType === "STUDENT") {
    return {
      kicker: "Student AI",
      title: "AI Study Assistant",
      subtitle: "Get explanations, hints, revision questions and study guidance.",
      icon: "bi-mortarboard",
      placeholder: "Example: Explain fractions to me with a simple example.",
      guardrail: "The assistant helps you learn. It should guide you, not do assignments for copying.",
      prompts: [
        "Explain today's topic in simple words.",
        "Give me 5 revision questions for this topic.",
        "Help me understand this homework question without giving me the full answer.",
        "Give me a study plan for this week.",
      ],
    };
  }

  if (userType === "TEACHER") {
    return {
      kicker: "Teacher AI",
      title: "AI Teaching Assistant",
      subtitle: "Draft lesson ideas, quiz questions, revision notes and learner-friendly explanations.",
      icon: "bi-person-workspace",
      placeholder: "Example: Create a 30-minute ICT lesson plan for Grade 3.",
      guardrail: "Review all AI-generated content before using it with learners.",
      prompts: [
        "Create a lesson plan for my next class.",
        "Generate 10 quiz questions with answers.",
        "Draft a short parent notice.",
        "Create learner-friendly notes for this topic.",
      ],
    };
  }

  if (userType === "PARENT") {
    return {
      kicker: "Parent AI",
      title: "Parent Assistant",
      subtitle: "Understand school notices, reports, fee statements and how to support your child.",
      icon: "bi-people",
      placeholder: "Example: Help me understand how to support my child in Mathematics.",
      guardrail: "The assistant gives guidance, but official school matters should still be confirmed with the school office.",
      prompts: [
        "Explain how I can support my child after a weak report.",
        "Help me understand a school announcement.",
        "Suggest a simple home study routine.",
        "Explain what questions I should ask the teacher.",
      ],
    };
  }

  return {
    kicker: "School AI",
    title: "School AI Assistant",
    subtitle: "Draft notices, summaries, letters, reports and school communications.",
    icon: "bi-stars",
    placeholder: "Example: Draft a parent notice about a sports day.",
    guardrail: "Review all AI-generated content before sending it officially.",
    prompts: [
      "Draft a school announcement.",
      "Summarise this update for parents.",
      "Create a professional email.",
      "Generate a short report summary.",
    ],
  };
}

export default SchoolAIPage;
